import React, { useLayoutEffect, useRef, useState } from "react";
import { ArrowRight, Check, ChevronLeft, ChevronRight, Loader2, Search, X } from "lucide-react";
import { MeshBackdrop, SPAZI } from "./brand";

/**
 * I pezzi comuni alle pagine "a casella" (Contatti, Conversazioni): elenco a
 * sinistra, dettaglio a destra, intestazione di una riga. Stanno qui cosi' le
 * pagine restano uguali per costruzione, non per copia.
 */

// I pulsanti della sezione: grigio chiaro, e scuro per l'azione principale.
export const BOTTONE =
  "inline-flex h-9 items-center justify-center gap-1.5 rounded-[10px] px-3 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";
export const GRIGIO = `${BOTTONE} bg-[#F1F5F9] text-[#18181B] hover:bg-[#E2E8F0]`;
export const SCURO = `${BOTTONE} bg-[#18181B] text-white hover:bg-black`;

/** Schermo largo: elenco e dettaglio affiancati. Sotto, uno alla volta. */
export function schermoLargo() {
  return typeof window.matchMedia === "function" && window.matchMedia("(min-width: 1024px)").matches;
}

// La pillola del titolo di sezione, la stessa di Apps & Integrazioni: fondo
// viola chiaro, icona e testo viola.
export const PILLOLA_SEZIONE =
  "inline-flex max-w-full items-center gap-2 rounded-[20px] bg-[rgba(173,70,255,0.12)] py-1 pl-2.5 pr-3.5 text-sm font-semibold tracking-[0.07px] text-[#A855F7]";

/**
 * L'intestazione: una riga sola, perche' qui contano gli elementi
 * dell'elenco. Il titolo e' la pillola viola, come in Apps & Integrazioni.
 * Lo sfondo della sezione resta come velatura corta.
 */
export function IntestazioneCasella({ icona: Icona, titolo, dettagli, azioni }) {
  return (
    <div className="relative">
      <MeshBackdrop variante="onboarding" spazio={24} coda={260} />
      <header className="pu-fade-up mx-auto flex max-w-[1400px] flex-col gap-3 pb-6 pt-2 sm:flex-row sm:items-center sm:justify-between sm:pt-4">
        <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
          <h1 className={PILLOLA_SEZIONE}>
            <Icona className="h-4 w-4 shrink-0" strokeWidth={2} aria-hidden="true" />
            <span className="truncate">{titolo}</span>
          </h1>
          {dettagli}
        </div>
        {azioni ? <div className="flex shrink-0 flex-wrap gap-2">{azioni}</div> : null}
      </header>
    </div>
  );
}

/** Un interruttore a piu' posizioni (es. In arrivo / Archiviati), con i conteggi. */
export function Interruttore({ voci, valore, onCambia, etichetta }) {
  return (
    <div role="group" aria-label={etichetta} className="inline-flex self-start rounded-[12px] bg-[#F1F5F9] p-1">
      {voci.map((voce) => {
        const attiva = valore === voce.valore;
        return (
          <button
            key={voce.valore}
            type="button"
            aria-pressed={attiva}
            onClick={() => onCambia(voce.valore)}
            className={`inline-flex h-8 items-center gap-1.5 rounded-[9px] px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              attiva ? "bg-white text-[#020617] shadow-[0_1px_2px_rgba(15,23,42,0.08)]" : "text-[#64748B] hover:text-[#020617]"
            }`}
          >
            {voce.etichetta}
            {voce.numero !== null && voce.numero !== undefined ? (
              <span className={`text-xs tabular-nums ${attiva ? "text-[#64748B]" : "text-[#94A3B8]"}`}>{voce.numero}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export function Ricerca({ valore, onCambia, segnaposto, etichetta }) {
  return (
    <div className="relative w-full sm:w-80">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" aria-hidden="true" />
      <input
        type="search"
        value={valore}
        onChange={(e) => onCambia(e.target.value)}
        onKeyDown={(e) => e.key === "Escape" && onCambia("")}
        placeholder={segnaposto}
        aria-label={etichetta}
        className="h-10 w-full rounded-lg border border-black/10 bg-white/80 pl-9 pr-9 text-sm text-[#020617] outline-none backdrop-blur placeholder:text-[#94A3B8] focus:border-[#94A3B8] [&::-webkit-search-cancel-button]:hidden"
      />
      {valore ? (
        <button
          type="button"
          onClick={() => onCambia("")}
          aria-label="Cancella la ricerca"
          className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-[#94A3B8] hover:bg-[#F1F5F9] hover:text-[#334155]"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

/** In fondo all'elenco: "1–25 di 60" e le frecce, solo se le pagine sono piu' d'una. */
export function Paginazione({ paginazione, perPagina, onPagina, cosa }) {
  const { page = 1, pages = 1, total = 0, limit = perPagina } = paginazione || {};
  if (pages <= 1) return null;
  const da = (page - 1) * limit + 1;
  const a = Math.min(total, page * limit);
  const freccia =
    "flex h-8 w-8 items-center justify-center rounded-[8px] hover:bg-[#F1F5F9] disabled:opacity-40";
  return (
    <div className="flex items-center justify-between border-t border-[#E2E8F0] px-4 py-2 text-xs text-[#64748B]">
      <span className="tabular-nums">
        {da}–{a} di {total}
      </span>
      <span className="flex gap-1">
        <button type="button" aria-label={`${cosa} più recenti`} disabled={page <= 1} onClick={() => onPagina(page - 1)} className={freccia}>
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>
        <button type="button" aria-label={`${cosa} meno recenti`} disabled={page >= pages} onClick={() => onPagina(page + 1)} className={freccia}>
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </span>
    </div>
  );
}

/** Gli elementi a gruppi consecutivi con lo stesso nome (es. per giorno), nell'ordine dato. */
export function raggruppa(elementi, nomeDi) {
  const gruppi = [];
  for (const elemento of elementi) {
    const nome = nomeDi(elemento);
    if (gruppi.at(-1)?.nome !== nome) gruppi.push({ nome, elementi: [] });
    gruppi.at(-1).elementi.push(elemento);
  }
  return gruppi;
}

/**
 * La barra per salvare, uguale in tutte le pagine con impostazioni (Widget,
 * Impostazioni, pagine delle app): ferma in basso, lo stato a sinistra e le
 * azioni a destra.
 *
 * Con onAnnulla, finche' ci sono modifiche compare "Annulla le modifiche".
 * Sull'app spenta che dopo il salvataggio sara' pronta (attiva false,
 * puoAttivare true) c'e' anche "Salva e attiva", come azione principale.
 */
export function BarraSalvataggio({
  modificato,
  saving,
  onSalva,
  salvaDisabilitato = false,
  onAnnulla,
  attiva,
  puoAttivare,
  onSalvaEAttiva,
  testi,
}) {
  const T = {
    nonSalvate: "Modifiche non salvate",
    salvato: "Tutto salvato",
    annulla: "Annulla le modifiche",
    salva: "Salva",
    salvaEAttiva: "Salva e attiva",
    ...testi,
  };
  const conAttiva = Boolean(onSalvaEAttiva) && !attiva && puoAttivare;
  const rotella = <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />;
  const spazio = useSpazioFinoAlFondo();
  return (
    <>
    {/* Con poco contenuto questo spazio porta la barra in fondo allo schermo
        invece di lasciarla a meta' pagina; con tanto contenuto e' 0. */}
    <div ref={spazio.ref} aria-hidden="true" style={{ height: spazio.altezza, marginTop: 0 }} />
    <div className={`sticky bottom-4 z-20 ${SPAZI.sopraBarraSalvataggio} flex flex-wrap items-center justify-between gap-3 rounded-[14px] border border-[#E2E8F0] bg-white/95 px-4 py-3 shadow-[0_10px_30px_rgba(15,23,42,0.10)] backdrop-blur`}>
      <span className="flex items-center gap-2 text-sm text-[#475569]">
        {modificato ? (
          <>
            <span className="h-2 w-2 rounded-full bg-amber-500" aria-hidden="true" />
            {T.nonSalvate}
          </>
        ) : (
          <>
            <Check className="h-4 w-4 text-[#0F7A3D]" aria-hidden="true" />
            {T.salvato}
          </>
        )}
      </span>
      <span className="flex flex-wrap gap-2">
        {onAnnulla && modificato ? (
          <button type="button" className={GRIGIO} onClick={onAnnulla} disabled={saving}>
            {T.annulla}
          </button>
        ) : null}
        <button
          type="button"
          className={conAttiva ? GRIGIO : SCURO}
          onClick={onSalva}
          disabled={!modificato || saving || salvaDisabilitato}
        >
          {saving && !conAttiva ? rotella : null}
          {T.salva}
        </button>
        {conAttiva ? (
          <button type="button" className={SCURO} onClick={onSalvaEAttiva} disabled={saving}>
            {saving ? rotella : null}
            {T.salvaEAttiva}
            {!saving ? <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" /> : null}
          </button>
        ) : null}
      </span>
    </div>
    </>
  );
}

/**
 * L'altezza che serve, prima della barra, per farla arrivare in fondo allo
 * schermo (16px dal bordo, come bottom-4). Si ricalcola quando cambia il
 * contenuto sopra (ResizeObserver sul contenitore) o la finestra.
 */
function useSpazioFinoAlFondo() {
  const ref = useRef(null);
  const [altezza, setAltezza] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    const barra = el?.nextElementSibling;
    if (!el || !barra) return undefined;
    const calcola = () => {
      // Posizione nella pagina con offsetTop: ignora le animazioni d'entrata
      // (translateY), che getBoundingClientRect conterebbe.
      let inizio = 0;
      for (let n = el; n; n = n.offsetParent) inizio += n.offsetTop;
      const margine = parseFloat(window.getComputedStyle(barra).marginTop) || 0;
      const manca = window.innerHeight - 16 - barra.offsetHeight - margine - inizio;
      setAltezza(Math.max(0, Math.floor(manca)));
    };
    calcola();
    window.addEventListener("resize", calcola);
    const osserva = typeof ResizeObserver !== "undefined" ? new ResizeObserver(calcola) : null;
    // Il contenitore (cambia il contenuto sopra) e la pagina (cambia qualcosa
    // piu' in alto, es. i font appena caricati).
    if (osserva && el.parentElement) osserva.observe(el.parentElement);
    if (osserva) osserva.observe(document.body);
    return () => {
      window.removeEventListener("resize", calcola);
      osserva?.disconnect();
    };
  }, []);
  return { ref, altezza };
}

/** A destra, prima di scegliere un elemento dall'elenco. */
export function NessunaScelta({ testo }) {
  return (
    <div className="hidden min-h-[320px] flex-col items-center justify-center gap-2 rounded-[14px] border border-dashed border-[#CBD5E1] bg-white/60 px-6 text-center lg:flex">
      <p className="text-sm font-medium text-[#334155]">{testo}</p>
      <p className="text-xs text-[#94A3B8]">
        Con le frecce <kbd className="rounded border border-[#E2E8F0] bg-white px-1 font-sans">↑</kbd>{" "}
        <kbd className="rounded border border-[#E2E8F0] bg-white px-1 font-sans">↓</kbd> passi da uno all'altro.
      </p>
    </div>
  );
}
